import React, { useState } from 'react';
import AurumButton from '../oracle/AurumButton';
import GhostButton from '../oracle/GhostButton';
import { COPY } from '../../contracts/copyMap';
import { ADHERENCE_VALUES, ADHERENCE_META } from '../../contracts/routineApi';

// "Did you use these?" — asked BEFORE the scan runs, not after.
//
// It has to come first: runOraclePipeline is a single awaited callable, so the
// answers must ride in the request. Asking afterwards would mean a second
// round trip and a score that changes under the user after they have seen it.
//
// The control is deliberately the SAME one from RoutineCard — same three
// values, same labels, same dot colours, straight out of ADHERENCE_META. The
// capture moved surfaces; the affordance did not, so nobody has to learn it
// twice.
//
// SKIP submits nothing at all (not a row of "no"). Declining to answer and
// declaring non-use are different statements, and only one of them should
// carry a scoring consequence.
//
// ── THE LIST SCROLLS; THE BUTTONS DO NOT ───────────────────────────────────
//
// A regimen can be a dozen actives, and this used to be one long column: on
// desktop it ran straight out of the bottom of the 587px capture card, and on
// mobile it ran off the bottom of the phone. Either way CONFIRM AND ANALYZE
// moved with the content, and on a long regimen it was off-screen entirely —
// the step asked a question with no reachable way to answer it.
//
// Header and actions are pinned, only the list between them scrolls, so the
// primary action sits in the same place whether the user has two actives or
// twelve.
export default function ProductUsageStep({ actives, onConfirm, onSkip }) {
  const [answers, setAnswers] = useState({});

  const setValue = (activeId, value) => {
    setAnswers((prev) => ({ ...prev, [activeId]: value }));
  };

  const entries = Object.entries(answers).map(([active_id, value]) => ({ active_id, value }));

  return (
    // w-full, NOT shrink-to-fit. `truncate` sets white-space:nowrap, so in a
    // shrink-to-fit parent the widest untruncated product name became the width
    // of the whole step — which the mobile overlay then centred, pushing half
    // of it off the left edge. Filling the parent is what lets truncate do its
    // job instead of causing the overflow it exists to prevent.
    <div
      className="flex h-full w-full min-h-0 max-w-lg mx-auto flex-col gap-4"
      data-testid="product-usage-step"
    >
      {/* No [BEFORE WE READ] Censor Bar. It repeated, in hard black caps, a
          label the portrait's own status pill was already showing beside it,
          and it cost the scrolling list a row. The headline says what this is. */}
      <div className="shrink-0 flex flex-col items-center gap-3 text-center" data-testid="usage-intro">
        <h2 className="font-display text-2xl text-bone m-0">{COPY.USAGE_STEP_HEADLINE}</h2>
        <p className="font-display text-bone-dim text-sm m-0">{COPY.USAGE_STEP_BODY}</p>
      </div>

      <ul className="list-none p-0 m-0 min-h-0 flex-1 overflow-y-auto flex flex-col gap-3">
        {actives.map((active) => (
          <li
            key={active.active_id}
            className="shrink-0 min-w-0 bg-[#161615] border border-hairline-soft rounded-card p-4"
          >
            <div className="font-display uppercase text-[11px] tracking-[0.18em] text-bone">
              {active.active}
            </div>
            {active.product && (
              <div className="font-display italic text-bone-dim text-xs mt-1 truncate">
                {active.product}
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label={active.active}>
              {ADHERENCE_VALUES.map((value) => {
                const meta = ADHERENCE_META[value];
                const selected = answers[active.active_id] === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={`${active.active}: ${meta.label}`}
                    onClick={() => setValue(active.active_id, value)}
                    className={[
                      'flex min-w-0 items-center gap-2 bg-transparent rounded-full px-3 py-2 cursor-pointer',
                      'font-display text-[11px] tracking-[0.06em] whitespace-nowrap',
                      'transition-all duration-200 ease-mogged',
                      selected
                        ? 'border border-aurum text-aurum-bright bg-aurum-faint'
                        : 'border border-[rgba(255,255,255,0.22)] text-bone-dim hover:border-[rgba(255,255,255,0.5)]',
                    ].join(' ')}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ backgroundColor: meta.dot }}
                      aria-hidden="true"
                    />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </li>
        ))}
      </ul>

      <div className="shrink-0 flex flex-col gap-2" data-testid="usage-actions">
        <AurumButton
          onClick={() => onConfirm(entries)}
          fullWidth
          data-testid="usage-confirm"
        >
          {COPY.USAGE_STEP_CONFIRM_CTA}
        </AurumButton>
        {/* No guilt copy, no penalty — see the note above. `justify-center`
            because the button stretches to the column width, so without it the
            label sits hard left under a centred primary. */}
        <GhostButton onClick={onSkip} className="justify-center" data-testid="usage-skip">
          {COPY.USAGE_STEP_SKIP_CTA}
        </GhostButton>
      </div>
    </div>
  );
}
